import React, { useState, useEffect } from 'react'
import { object, array, bool } from 'prop-types'
import { createComponent } from '@ycos/fela'
import { constructUrl } from '@ynap/url-configuration'
import { Picture } from '@ycos/picture'
import CallToAction from '@ycos/component-cta'
import { getFontStyles } from '@ycos/primitives'
import { PictureHoverState, ImageContentWrapper, ImageHolder } from './image'
import { getProductApiUrl, getProductApiHeaders, getProductsListParameters } from './utils/api'
import { getImgUrlFromProduct } from './utils/image'
import { hasTag } from '@ycos/coremedia-renderer'
import { trackOnProductClick } from './analytics'

const MobileBreakpoint = createComponent('MobileBreakpoint', () => ({
  display: 'block',
  'screen-medium': {
    display: 'none'
  }
}))

const DesktopBreakpoint = createComponent('DesktopBreakpoint', () => ({
  display: 'none',
  'screen-medium': {
    display: 'block'
  }
}))

const Container = createComponent('Container', ({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  gap: `${theme.spacingMultiplier}px`,
  direction: 'ltr'
}))

const GridLayout = createComponent(
  'GridLayout',
  ({ layout = 'mobile', theme }) => {
    return {
      display: 'grid',
      gridTemplateColumns: `repeat(${layout === 'mobile' ? 3 : 5}, 1fr)`,
      gridTemplateRows: `repeat(${layout === 'mobile' ? 3 : 2}, 1fr)`,
      /* Hide all other items which don't fit in the grid */
      gridAutoRows: 0,
      overflow: 'hidden',
      /* reset ul styles  */
      margin: `${theme.spacingMultiplier * 2}px 0`,
      padding: 0,
    }
  },
  'ul'
)

const GridItem = createComponent(
  'GridItem',
  () => ({
    margin: 0,
    padding: 0,
    listStyleType: 'none'
  }),
  'li'
)

const CtaContainer = createComponent(
  'CtaContainer',
  () => ({
    display: 'flex',
    justifyContent: 'center',
    width: '100%'
  }),
  'div'
)

const CTA_MAX_WIDTH = '326px'
const CtaButtonContainer = createComponent(
  'CtaButtonContainer',
  ({ theme }) => {
    return {
      ...getFontStyles(theme.typography, {
        type: 'Label',
        name: '1'
      }),
      fontWeight: 400,
      width: '100%',
      'screen-medium': {
        width: CTA_MAX_WIDTH
      },
      'screen-small-max': {
        '& button': {
          height: '48px',
          maxWidth: '100%'
        }
      }
    }
  },
  CallToAction,
  ['item']
)

export const Status = {
  NOTLOADED: 'notloaded',
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error'
}

const Header = createComponent('Header', ({ theme, brand, locale }) => {
  const customStyles = {
    color: theme.typography.colors.PrimaryBlack,
    ...theme.custom.loyaltyProductsGrid.title
  }

  const defaultStyles = {
    ...getFontStyles(theme.typography, {
      type: 'Heading',
      tagName: 'h4',
      name: brand === 'mrp' && locale.language === 'ar' ? 'A0' : 'A5'
    }),
    ...customStyles
  }

  defaultStyles['screen-large'] = {
    ...defaultStyles['screen-large'],
    ...customStyles
  }

  return defaultStyles
})

const Text = createComponent('Text', ({ theme }) => ({
  ...getFontStyles(theme.typography, {
    type: 'Paragraph',
    tagName: 'p',
    name: '3'
  }),
  color: theme.typography.colors.PrimaryBlack,
  fontWeight: theme.custom.loyaltyBanner.paragraph.fontWeight,
  fontSize: theme.custom.loyaltyBanner.paragraph.fontSize,
  lineHeight: theme.custom.loyaltyBanner.paragraph.lineHeight,
  whiteSpace: 'pre-line',
  textAlign: 'center'
}))

const ItemCount = createComponent('ItemCount', ({ theme }) => {
  const defaultStyles = {
    ...getFontStyles(theme.typography, {
      type: 'Label',
      tagName: 'h3',
      name: '7'
    }),
    ...theme.custom.loyaltyProductsGrid.itemCount,
    color: theme.typography.colors.SecondaryGreyMetal
  }
  defaultStyles['screen-large'] = {
    ...defaultStyles['screen-large'],
    ...defaultStyles
  }

  return defaultStyles
})

const FETCH_ERROR_MESSAGE = 'Cannot fetch products from API'

const GridItems = ({ products, locale, urlConfig, imageSphere, lazy, item, brand }) => {
  if (!products) {
    return null
  }

  return products.map((product, index) => {
    const { seo, productColours, productUrl } = product

    const productSEO = productColours?.find((color) => color.selected)?.seo || productColours?.[0]?.seo || seo
    const templateParams = {
      LOCALE: locale.id,
      SEO_URLKEYWORD: productSEO?.seoURLKeyword || productUrl
    }
    const productUrlTemplate = urlConfig.pdp.construct
    const productLink = constructUrl(productUrlTemplate, templateParams)
    const inImgUrl = getImgUrlFromProduct(product, 'in', imageSphere)
    const images = [
      {
        type: 'image',
        imageUrl: inImgUrl
      }
    ]


    return (
      <GridItem key={product.partNumber}>
        <a href={productLink} onClick={trackOnProductClick(item, product, index, brand)}>
          <ImageContentWrapper>
            <PictureHoverState />
            <Picture
              imagesphere={imageSphere}
              images={images}
              position={'relative'}
              style={{
                display: 'block',
                width: '100%',
                maxWidth: '100%',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
              lazy={lazy}
            />
            {!inImgUrl && <ImageHolder />}
          </ImageContentWrapper>
        </a>
      </GridItem>
    )
  })
}

const LoyaltyProductsGrid = (props) => {
  const { productsData: initialProductsData, urlConfig, locale, item, brand, config, lazy, onTargetClick } = props
  const { ynapParameter, target, tags } = item
  const hideItemCount = hasTag(tags, 'LoyaltyProductsGrid.HideItemCount')
  const { imageSphere } = config

  const [productsData, setProductsData] = useState(initialProductsData)

  const [loadingState, setLoadingState] = useState(!productsData || productsData.length === 0 ? Status.NOTLOADED : Status.LOADED)

  useEffect(() => {
    if (initialProductsData) {
      setProductsData(initialProductsData)
      setLoadingState(Status.LOADED)
    }
  }, [initialProductsData])

  useEffect(() => {
    let isSubscribed = true

    if (!productsData) {
      const fetchProducts = async (url, config) => {
        const headers = getProductApiHeaders(config)
        const response = await fetch(url, {
          headers: { ...headers }
        })

        const result = await response.json()

        const { products, recordSetTotal } = result
        return { products, recordSetTotal }
      }

      const productListParameters = getProductsListParameters(ynapParameter)
      const { wcs } = config
      const url = getProductApiUrl({
        locale,
        wcs,
        brand,
        params: productListParameters
      })
      setLoadingState(Status.LOADING)
      fetchProducts(url, { ...config }, setLoadingState)
        .then((response) => {
          if (isSubscribed) {
            setProductsData(response)
            setLoadingState(Status.LOADED)
          }
        })
        .catch(() => {
          if (isSubscribed) {
            setLoadingState(Status.ERROR)
            // eslint-disable-next-line no-console
            console.error(FETCH_ERROR_MESSAGE)
          }
        })
    }
    return () => {
      isSubscribed = false
    }
  }, [productsData])

  if ([Status.LOADING, Status.ERROR].includes(loadingState) || !productsData?.products?.length) {
    return null
  }

  return (
    <Container>
      {!hideItemCount ? (
        <ItemCount>{`${productsData.recordSetTotal} ${item.subTitlePlain}`}</ItemCount>
      ) : null}
      <Header locale={locale} brand={brand?.brandId}>{item.teaserTitle}</Header>
      <Text>{item.teaserTextPlain}</Text>
      <MobileBreakpoint>
        <GridLayout layout="mobile">
          <GridItems products={productsData.products} locale={locale} urlConfig={urlConfig} imageSphere={imageSphere} lazy={lazy} item={item} brand={brand} />
        </GridLayout>
      </MobileBreakpoint>
      <DesktopBreakpoint>
        <GridLayout layout="desktop">
          <GridItems products={productsData.products} locale={locale} urlConfig={urlConfig} imageSphere={imageSphere} lazy={lazy} item={item} brand={brand} />
        </GridLayout>
      </DesktopBreakpoint>
      {target ? (
        <CtaContainer onClick={onTargetClick}>
          <CtaButtonContainer item={{ layoutVariant: 'isc-cta-primary', href: target.href, teaserTitle: target.teaserTitle }} />
        </CtaContainer>
      ) : null}
    </Container>
  )
}

LoyaltyProductsGrid.layoutVariant = 'isc-loyalty-products-grid'

LoyaltyProductsGrid.propTypes = {
  urlConfig: object,
  locale: object,
  brand: object,
  item: object.isRequired,
  productsData: array,
  lazy: bool
}

LoyaltyProductsGrid.defaultProps = {
  urlConfig: {
    pdp: {
      construct: '/{{LOCALE}}/shop/product{{SEO_URLKEYWORD}}'
    }
  },
  locale: {
    id: 'en-gb'
  },
  brand: {
    brandId: 'nap'
  },
  lazy: true
}

export default LoyaltyProductsGrid
